import {
  ArrowLeft,
  Calendar as CalendarIcon,
  ChevronDown,
  Clock,
  Mail,
  Pencil,
  Phone,
  Plus,
  Star,
  User as UserIcon,
  Users,
  Watch,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader } from "@/components/ui/Card";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import warning from "@/assets/warning.svg";
import { Textarea } from "@/components/ui/TextArea";
import { TicketAction, TicketStatus, useCancelTicketMutation, useGetUserTicketDetailQuery, useUpdateLinkedTicketMutation, useUpdateTitleMutation } from "@/services/LiveQueueService";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import {
  calculateWaitingTime,
  formatDate,
  formatPhoneNumber,
  formatTime,
  getUserStatusColor,
  statusLabels,
} from "@/utils/Helper";
import { Comment } from "@/app/types/tickets";
import React, { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { toast } from "@/components/ui/UseToast";
import { messaging } from "@/utils/lib/Firebase";
import { Messaging, onMessage } from "firebase/messaging";
import { useGenerateUserTicketForServiceMutation, useGetAllDepartmentServiceByIdQuery, useLazyGetAllDepartmentServiceByIdQuery } from "@/services/SelfServiceKiosk";
import { Ticket } from "lucide-react";
import { Label } from "@/components/ui/Label";
import { Input } from "@/components/ui/Input";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/Command";
import { X } from "lucide-react";
import { Service } from "@/app/types/service";
import TicketAvailabilityCalendar from "@/components/remote-user/user-portal/components/TicketAvailabilityCalendar";
import TicketForm, { FormValues } from "@/components/remote-user/user-portal/components/TicketForm";
const getStatusColor = (status: TicketStatus) => {
  switch (status) {
    case TicketStatus.NEW:
      return "bg-[#1976D2] hover:bg-blue-600"; // Dark Blue
    case TicketStatus.ARRIVED:
      return "bg-[#388E3C] hover:bg-green-600"; // Dark Green
    case TicketStatus.CHECKED_IN:
      return "bg-[#FBC02D] hover:bg-yellow-600"; // Deep Yellow
    case TicketStatus.NO_SHOW:
      return "bg-[#D32F2F] hover:bg-red-700"; // Dark Red
    case TicketStatus.IN_PROGRESS:
      return "bg-[#F57C00] hover:bg-orange-600"; // Deep Orange
    case TicketStatus.AWAITING_INFO:
      return "bg-[#5D4037] hover:bg-brown-700"; // Dark Brown
    case TicketStatus.HOLD:
      return "bg-[#455A64] hover:bg-gray-700"; // Steel Gray
    case TicketStatus.CANCELLED:
      return "bg-[#616161] hover:bg-gray-600"; // Neutral Gray
    case TicketStatus.COMPLETED:
      return "bg-[#2E7D32] hover:bg-green-700"; // Darker Green
    case TicketStatus.ARCHIVED:
      return "bg-[#37474F] hover:bg-gray-800"; // Dark Gray
    case TicketStatus.RESERVED:
      return "bg-[#00838F] hover:bg-cyan-700"; // Teal
    default:
      return "bg-gray-500 hover:bg-gray-600"; // Default Gray
  }
};

interface Schedule {
  day: string;
  opening_time: string;
  closing_time: string;
  ticket_end_time: string;
}

export const renderStars = (rating: number) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <Star
        key={i}
        className={`w-4 h-4 ${i <= rating ? "text-primary fill-primary" : "text-gray-300"}`}
      />
    );
  }
  return stars;
};

export default function UserTicketDetails() {
  const [isCreatingLinkedTicket, setIsCreatingLinkedTicket] = useState(false);
  const [customTags, setCustomTags] = useState<string[]>([]);
  const [isCreateTicketOpen, setIsCreateTicketOpen] = useState(false);
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);

  const User = useSelector((state: RootState) => state.auth.userInfo) ?? 0;
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);

  const { id, name, serviceId, serviceName } = useParams();
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(
    null
  );
  const [service, setService] = React.useState<Service | null>(null);
  const [generateTicket, { isLoading: isTicketGenerating, isError }] =
    useGenerateUserTicketForServiceMutation();
  const [updateTitle, { isLoading: isTicketLoading }] =
    useUpdateTitleMutation();
  const [isupdatingTicket, setIsUpdatingTicket] = React.useState(false);

  const [cancelTicket, { isLoading: isTicketCancelling }] =
    useCancelTicketMutation();

  const { data, isLoading, error, refetch } = useGetUserTicketDetailQuery({
    ticketId: id ? parseInt(id, 10) : 0,
    userId: userInfo?.id ? parseInt(userInfo.id, 10) : 0,
  });

  const [addLinkedTicket, { isLoading: isLinkingTicket }] =
    useUpdateLinkedTicketMutation();

  // Add these new state variables
  const [isCommandOpen, setIsCommandOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [manualTagValue, setManualTagValue] = useState("");
  const {
    control: ticketcontrol,
    handleSubmit: tickethandleSubmit,
    reset,
    formState: { errors: tickethandleErrors },
    setValue:setUpdateTicketValue,
    watch:ticketupdateWatch
  } = useForm({
    defaultValues: {
      ticketId: 0,
      title: "",
      description: "",
      status: TicketStatus.NEW,
    },
  });

  const ticketUpdateWacther = ticketupdateWatch();

  const onHandleUpdateInfo = (data: {
    title: string;
    description: string;
    status: TicketStatus;
    ticketId: number;
  }) => {
    updateTitle({
      action: TicketAction.UPDATE_DETAILS,
      ticketId: data.ticketId,
      title: data.title,
      description: data.description,
    })
      .unwrap()
      .then(() => {
        toast({
          title: "Ticket Update",
          description: "Ticket Updated Successfully",
          variant: "success",
        });
        setIsUpdatingTicket(false);
        reset({
          title: "",
          description: "",
          status: TicketStatus.NEW,
          ticketId: 0,
        });
        refetch();
      })
      .catch(() => {
        toast({
          title: "Error",
          description: "Error cancelling ticket.",
          variant: "destructive",
        });
      });
  };

  const {
    control: rescheduleTicket,
    handleSubmit: rescheduleTicketHandleSubmit,
    formState: { errors: rescheduleTicketErros },
    reset: rescheduleReset,
    setValue,
    watch: rescheduleWatch,
  } = useForm<{
    previousTicket: number;
    title: string;
    description?: string;
    date: string; // or Date if you want a Date object
    time: string;
    previousTicketDate: string;
  }>();

  const rescheduleWatcher = rescheduleWatch();

  const createTicketForm = useForm<FormValues>({
    defaultValues: {
      service_tag: "",
      title: "",
      description: "",
      date: "",
      time: "",
    },
  });

  const rescheduleForm = useForm<FormValues>({
    defaultValues: {
      service_tag: "",
      title: "",
      description: "",
      date: "",
      time: "",
    },
  });

  const handleSubmitReschedule = async (data: {
    previousTicket: number;
    title: string;
    description?: string;
    date: string;
    time: string;
    previousTicketDate: string;
  }) => {
    // Ensure time is in HH:mm:ss format
    const formattedTime = (() => {
      // If no colon, assume it's just hour and add minutes and seconds
      if (!data.time.includes(":")) {
        return `${data.time}:00:00`;
      }

      // Split the time into parts
      const parts = data.time.split(":");

      // If HH:MM format, add seconds
      if (parts.length === 2) {
        return `${data.time}:00`;
      }

      // If already in HH:MM:SS format, return as is
      return data.time;
    })();

    if (!selectedServiceId) {
      toast({
        variant: "destructive",
        title: "No Service Selected",
        description: "Please select a service before submitting.",
      });
      return;
    }

    // Convert previous ticket date to Date object
    const previousTicketDate = new Date(data.previousTicketDate);
    const selectedDate = new Date(data.date);
    const today = new Date();

    // Check if rescheduling is for the same day
    const isReschedulingSameDay =
      previousTicketDate.toDateString() === today.toDateString() &&
      selectedDate.toDateString() === today.toDateString();

    // Convert user input time to Date object
    const [userHour, userMin] = data.time.split(":").map(Number);
    const userSelectedTime = new Date(selectedDate);
    userSelectedTime.setHours(userHour, userMin, 0, 0);

    // ✅ New Check: Prevent booking a ticket for a past time on the same day
    if (selectedDate.toDateString() === today.toDateString()) {
      const currentHour = today.getHours();
      const currentMin = today.getMinutes();
      const currentTime = new Date(today);
      currentTime.setHours(currentHour, currentMin, 0, 0);

      if (userSelectedTime < currentTime) {
        toast({
          variant: "destructive",
          title: "Invalid Time Selection",
          description:
            "The selected time has already passed for today. Please choose a future time or a different date.",
        });
        return;
      }
    }

    try {
      if (User && User.id) {
        // Fetch selected service schedule
        const selectedService = servicesData?.data.find(
          (service) => service.id === selectedServiceId
        );
        if (!selectedService) {
          toast({
            variant: "destructive",
            title: "Service Not Found",
            description: "Could not find the selected service schedule.",
          });
          return;
        }

        // Convert selected date to a Date object
        const selectedDate = new Date(data.date);
        const selectedDay = selectedDate.toLocaleString("en-US", {
          weekday: "long",
        });

        // Find the service schedule for the selected day
        const serviceSchedule = selectedService.schedules.find(
          (s) => s.day === selectedDay
        );

        if (!serviceSchedule) {
          toast({
            variant: "destructive",
            title: "Service Unavailable",
            description: "This service is not available on the selected date.",
          });
          return;
        }

        // Convert opening, closing, and ticket end times to Date objects
        const [openHour, openMin] = serviceSchedule.opening_time
          .split(":")
          .map(Number);
        const [closeHour, closeMin] = serviceSchedule.closing_time
          .split(":")
          .map(Number);
        const [ticketEndHour, ticketEndMin] = serviceSchedule.ticket_end_time
          .split(":")
          .map(Number);

        const openingTime = new Date(selectedDate);
        openingTime.setHours(openHour, openMin, 0, 0);

        const closingTime = new Date(selectedDate);
        closingTime.setHours(closeHour, closeMin, 0, 0);

        const ticketEndTime = new Date(selectedDate);
        ticketEndTime.setHours(ticketEndHour, ticketEndMin, 0, 0);

        // Convert user input time to Date object
        const [userHour, userMin] = data.time.split(":").map(Number);
        const userSelectedTime = new Date(selectedDate);
        userSelectedTime.setHours(userHour, userMin, 0, 0);

        const today = new Date();
        const isSameDay = selectedDate.toDateString() === today.toDateString();

        if (isReschedulingSameDay) {
          // console.log("userSelectedTime:", userSelectedTime);
          // console.log("ticketEndTime:", ticketEndTime);
          // console.log("currentTime:", today);
          // console.log("openingTime:", openingTime);
          // console.log("closingTime:", closingTime);
          // console.log("selectedDate:", selectedDate);
          // console.log("verify here",userSelectedTime> ticketEndTime);
          // ✅ Case 1: User selects a time outside service hours
          if (userSelectedTime > ticketEndTime) {
            toast({
              variant: "destructive",
              title: "Service Closed for the Day",
              description:
                "Service is closed for the day. Please reschedule for a future date.",
            });
            return;
          }
        }
        // ✅ Case 1: User selects a time outside service hours
        if (
          userSelectedTime < openingTime ||
          userSelectedTime > ticketEndTime
        ) {
          toast({
            variant: "destructive",
            title: "Invalid Time",
            description: `Selected time must be within service hours: ${openHour % 12 || 12}:${String(openMin).padStart(2, "0")} ${openHour >= 12 ? "PM" : "AM"} - ${ticketEndHour % 12 || 12}:${String(ticketEndMin).padStart(2, "0")} ${ticketEndHour >= 12 ? "PM" : "AM"},
          or please book an appointment for future date.`,
          });
          return;
        }

        // ✅ Case 2: Same-day ticket after ticket end time → ❌ Not allowed
        if (isSameDay && userSelectedTime > ticketEndTime) {
          toast({
            variant: "destructive",
            title: "Service Closed for the Day",
            description:
              "Service is closed for the day, please book an appointment for a later date.",
          });
          return;
        }

        // ✅ Case 3: Future booking outside service schedule → ❌ Not allowed
        if (!isSameDay && !serviceSchedule) {
          toast({
            variant: "destructive",
            title: "Service Unavailable",
            description:
              "Service is not available on the selected date. Please choose another date.",
          });
          return;
        }
        // Prepare payload for API submission
        const payload = {
          title: data.title,
          description: data.description || "",
          date: data.date,
          time: `${formattedTime}`,
          service_id: selectedServiceId,
          user_id: Number(User.id),
          eta: 40,
        };

        if (isCreatingLinkedTicket) {
          await generateTicket({
            ...payload,
          })
            .unwrap()
            .then(async (res) => {
              if (
                res?.detail === "You already have a pending ticket for today"
              ) {
                toast({
                  variant: "destructive",
                  title: "Ticket Creation Failed",
                  description: "You already have a pending ticket for today.",
                });
                return;
              }
              
              await addLinkedTicket({
                action: TicketAction.LINK,
                ticket_id: Number(res.id),
                staff_id: String(userInfo?.id ?? 0),
                trigger_lambda: false,
                parentTicketId: Number(data.previousTicket),
              }).unwrap();

              rescheduleReset({
                title: "",
                description: "", // Optional field
                date: "",
                time: "",
                previousTicket: 0,
                previousTicketDate: "",
              });

              setIsETA(false);
              setIsCreatingLinkedTicket(false);
              refetch();
              toast({
                title: "Ticket Rescheduled",
                description: "Ticket Rescheduled Successfully",
                variant: "success",
              });
              navigate(`/user/ticket-listing`);
              // Format closing time properly
              
            })
            .catch((err) => {
              toast({
                variant: "destructive",
                title: "Failed to create Ticket",
                description:
                  err && "data" in err && typeof err.data === "object"
                    ? (err.data as any).detail ||
                      (err.data as any).message ||
                      "Something went wrong"
                    : "Something went wrong",
              });
            });
        } else {
          // Call API to generate ticket
          await generateTicket({
            ...payload,
          })
            .unwrap()
            .then(async (res) => {
              if (
                res?.detail === "You already have a pending ticket for today"
              ) {
                toast({
                  variant: "destructive",
                  title: "Ticket Creation Failed",
                  description: "You already have a pending ticket for today.",
                });
                return;
              }

              rescheduleReset({
                title: "",
                description: "", // Optional field
                date: "",
                time: "",
                previousTicket: 0,
                previousTicketDate: "",
              });

              setIsETA(false);
              cancelTicket({
                ticket_ids: [data.previousTicket],
                cancel_reason: "Ticket Scheduled to later date.",
              });
              toast({
                title: "Ticket Rescheduled",
                description: "Ticket Rescheduled Successfully",
                variant: "success",
              });
              navigate(`/user/ticket-listing`);
              // Format closing time properly
              const [resHour, resMin] = res?.closing_time
                .split(":")
                .map(Number);
              const closingTimeFormatted = `${resHour % 12 || 12}:${String(resMin).padStart(2, "0")} ${resHour >= 12 ? "PM" : "AM"}`;
            })
            .catch((err) => {
              toast({
                variant: "destructive",
                title: "Failed to create Ticket",
                description:
                  err && "data" in err && typeof err.data === "object"
                    ? (err.data as any).detail ||
                      (err.data as any).message ||
                      "Something went wrong"
                    : "Something went wrong",
              });
            });
        }
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error Generating Ticket",
        description:
          error?.data?.detail || error?.data?.message || "Something went wrong",
      });
    }
  };
  const [
    getServices,
    { data: selectServiceData, isLoading: isServicesLoading },
  ] = useLazyGetAllDepartmentServiceByIdQuery();

  const { data: servicesData } = useGetAllDepartmentServiceByIdQuery({
    department_id: Number(serviceId!),
    query: `service_name=${serviceName}&ignore_schedule=true&offset=0`,
  });

  const memoizedData = React.useMemo(() => {
    return servicesData;
  }, [servicesData]);

  useEffect(() => {
    if (memoizedData?.data && memoizedData.data[0] && memoizedData.data[0].id) {
      setSelectedServiceId(memoizedData.data[0].id);
      setService(memoizedData.data[0]);
    }
  }, [servicesData]);

  const location = useLocation(); // Get the state with the previous path
  const [isETA, setIsETA] = React.useState(false);
  const [iscancel, setIsCancel] = React.useState(false);

  const navigate = useNavigate();

  const handleOpenReschedule = async () => {
    setIsRescheduleOpen(true);
    rescheduleForm.reset({
      service_tag: "",
      title: "",
      description: data?.ticket_details.description ?? "",
      date: "",
      time: "",
    });
  };

  const handleCreateLinkedTicket = async () => {
    setIsCreateTicketOpen(true);
    createTicketForm.reset({
      service_tag: "",
      title: "",
      description: data?.ticket_details.description ?? "",
      date: "",
      time: "",
    });
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<{ cancel_reason: string }>();

  const onSubmit = (data: { cancel_reason: string }) => {
    // Handle cancel logic with the form data
    cancelTicket({
      ticket_ids: [id ? parseInt(id, 10) : 0],
      cancel_reason: data.cancel_reason,
    })
      .unwrap()
      .then(() => {
        toast({
          title: "Ticket Cancelled",
          description: "Ticket Cancelled Successfully",
          variant: "success",
        });
        setIsCancel(false);
        refetch();
      })
      .catch(() => {
        toast({
          title: "Error",
          description: "Error cancelling ticket.",
          variant: "destructive",
        });
      });
    // Add logic for ticket cancellation here
  };

  React.useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    messaging().then((msgInstance: Messaging | null) => {
      if (!msgInstance) {
        console.error("Firebase Messaging is not initialized.");
        return;
      }

      unsubscribe = onMessage(msgInstance, (payload) => {
        if (payload.notification) {
          toast({
            variant: "default",
            description: payload.notification.body,
            title: payload.notification.title,
          });
        }
        const { data } = payload;
        if (data?.type && data?.id) {
        }
      });
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

 const resetRescheduleForm = () => {
   rescheduleReset({
     title: "",
     description: "",
     date: "",
     time: "",
     previousTicket: 0,
     previousTicketDate: "",
   });
   setSearchValue("");
   setManualTagValue("");
 };

 // Add this function to handle tag selection
 const handleTagSelect = (value: string) => {
   setUpdateTicketValue("title", value);
   setValue("title", value);
   setSearchValue(value);
   setIsCommandOpen(false);
 };

 const clearCustomTagsFromStorage = () => {
   localStorage.removeItem("customTags");
   setCustomTags([]);
 };

const handleManualTagSubmit = () => {
  if (manualTagValue.trim()) {
    const trimmedValue = manualTagValue.trim();
    setUpdateTicketValue("title", trimmedValue);
    setSearchValue(trimmedValue);
    setValue("title", trimmedValue);

    // Check if the tag doesn't exist in either custom tags or service tags
    if (
      !customTags.some(
        (tag) => tag.toLowerCase() === trimmedValue.toLowerCase()
      ) &&
      !(service?.tags || []).some(
        (tag) => tag.toLowerCase() === trimmedValue.toLowerCase()
      )
    ) {
      // Update the customTags state
      const updatedTags = [...customTags, trimmedValue];
      setCustomTags(updatedTags);

      // Save to localStorage
      localStorage.setItem("customTags", JSON.stringify(updatedTags));
    }

    setManualTagValue("");
    setIsCommandOpen(false);
  }
};


  useEffect(() => {
    // Load custom tags from localStorage when component mounts
    const savedTags = localStorage.getItem("customTags");
    if (savedTags) {
      setCustomTags(JSON.parse(savedTags));
    }
  }, []);


  if (isLoading) {
    return (
      <div className="p-4">
        <Card className="w-full max-w-2xl mx-auto">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="w-32 h-6 bg-gray-200 animate-pulse" />
              <div className="w-16 h-6 bg-gray-200 animate-pulse" />
            </div>
            <div className="space-y-1">
              <div className="flex items-baseline gap-2">
                <div className="w-32 h-8 bg-gray-200 animate-pulse" />
                <div className="w-16 h-6 bg-gray-200 animate-pulse" />
              </div>
              <div className="w-48 h-6 bg-gray-200 animate-pulse" />
              <div className="w-32 h-6 bg-gray-200 animate-pulse" />
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-200 animate-pulse" />
                <div className="space-y-1 w-48 h-6 bg-gray-200 animate-pulse" />
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-200 animate-pulse" />
                <div className="space-y-1 w-48 h-6 bg-gray-200 animate-pulse" />
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-200 animate-pulse" />
                <div className="space-y-1 w-48 h-6 bg-gray-200 animate-pulse" />
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-200 animate-pulse" />
                <div className="space-y-1 w-48 h-6 bg-gray-200 animate-pulse" />
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-200 animate-pulse" />
                <div className="space-y-1 w-48 h-6 bg-gray-200 animate-pulse" />
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-200 animate-pulse" />
                <div className="space-y-1 w-48 h-6 bg-gray-200 animate-pulse" />
              </div>
            </div>
            <div className="pt-4 space-y-4">
              <h3 className="font-semibold">Comments</h3>
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-gray-200 animate-pulse rounded-full" />
                <div className="space-y-1 w-48 h-6 bg-gray-200 animate-pulse" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center text-primary text-xl">
        Error loading ticket details.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-outletcolor">
      {/* Header */}
      <div className="">
        <div className="flex items-center p-4 max-w-2xl mx-auto">
          <button
            onClick={() => navigate(location.state?.from || "/")}
            className="rounded-full p-2 hover:bg-gray-100 bg-gray-200"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <h1 className="text-xl font-semibold ml-4">
            Ticket No - {name}{" "}
            {data?.ticket_details.r_ticket_number
              ? `-> (${data.ticket_details.r_ticket_number})`
              : ""}
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <main className="pt-2 pb-24 px-4 max-w-2xl mx-auto">
        <Card className="bg-[#FFFCF5] border-none shadow-sm">
          <CardHeader>
            <div className="flex flex-row justify-between items-center">
              <div></div>
              <div className="flex gap-4">
                {data?.ticket_details.status === TicketStatus.NEW ||
                data?.ticket_details.status === TicketStatus.ARCHIVED ||
                data?.ticket_details.status === TicketStatus.AWAITING_INFO ||
                data?.ticket_details.status === TicketStatus.HOLD ||
                data?.ticket_details.status === TicketStatus.RESERVED ? (
                  <Button
                    onClick={() => setIsCancel(true)}
                    className="bg-red-500 text-nowrap text-[12px] font-semibold hover:bg-red-600 text-white   rounded-lg w-full "
                  >
                    Cancel Ticket
                  </Button>
                ) : null}

                {data?.ticket_details.status === TicketStatus.NEW ||
                data?.ticket_details.status === TicketStatus.ARCHIVED ||
                data?.ticket_details.status === TicketStatus.AWAITING_INFO ||
                data?.ticket_details.status === TicketStatus.HOLD ||
                data?.ticket_details.status === TicketStatus.RESERVED ? (
                  <Button
                    onClick={handleOpenReschedule}
                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg w-full md:w-auto"
                  >
                    Reschedule
                  </Button>
                ) : null}

                {data?.ticket_details.status === TicketStatus.COMPLETED &&
                new Date() <=
                  new Date(
                    new Date(data.ticket_details.created_at).getTime() +
                      2 * 24 * 60 * 60 * 1000
                  ) ? (
                  <Button
                    onClick={() => navigate(`/user/feedback/${id}`)}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg w-full md:w-auto"
                  >
                    {data.ticket_details.ticket_feedback.feedback?.length
                      ? "Edit Feedback"
                      : "Give Feedback"}
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="flex flex-col gap-3 w-full justify-end items-end">
              <div></div>
              <div className="w-fit flex justify-between items-center">
                {data?.ticket_details.status === TicketStatus.COMPLETED &&
                  data?.ticket_details.parent_ticket === null && (
                    <Button
                      onClick={handleCreateLinkedTicket}
                      className="bg-red-500 text-nowrap text-[12px] font-semibold hover:bg-red-600 text-white rounded-lg w-full"
                    >
                      Create Ticket
                    </Button>
                  )}
              </div>
            </div>
            <div className="flex flex-row justify-between items-center">
              {" "}
              <h2 className="text-lg font-semibold">Details</h2>
            </div>

            <div className="flex items-center gap-2 justify-between w-full">
              <div className="w-full">
                <h1 className="text-xl font-semibold">
                  Ticket ID
                  <div> {data?.ticket_details.ticket_id}</div>
                </h1>
              </div>

              <div className="w-full flex justify-end">
                {data?.ticket_details.status === TicketStatus.NEW ||
                data?.ticket_details.status === TicketStatus.ARCHIVED ||
                data?.ticket_details.status === TicketStatus.AWAITING_INFO ||
                data?.ticket_details.status === TicketStatus.RESERVED ? (
                  <Button
                    variant="outline"
                    className="flex text-sm items-center gap-1 w-fit p-1"
                    onClick={() => {
                      setIsUpdatingTicket(true);
                      reset({
                        title: data?.ticket_details.title,
                        description: data?.ticket_details.description,
                        status: data?.ticket_details.status,
                        ticketId: data?.ticket_details.id,
                      });
                    }}
                  >
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Pencil className="h-5 w-5 text-[#B98E47]" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-sm text-black">Edit</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Button>
                ) : null}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Customer Info */}
            <div className="flex justify-between items-center">
              <h3 className="text-base text-black">
                ITS No: {data?.ticket_details.customer.username}
                <br></br>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="capitalize">
                        {data?.ticket_details.customer.name &&
                        data.ticket_details.customer.name.length > 40
                          ? data.ticket_details.customer.name.slice(0, 12) +
                            "..."
                          : data?.ticket_details.customer.name}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="capitalize">
                      {String(data?.ticket_details.customer.id) === "0"
                        ? ""
                        : data?.ticket_details.customer.name}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </h3>
              <span
                className={`px-3 py-1 capitalize ${data?.ticket_details.status ? getUserStatusColor(data.ticket_details.status) : ""} text-white text-sm rounded-full`}
              >
                {statusLabels[data?.ticket_details.status ?? TicketStatus.NEW]}
              </span>
            </div>

            {/* Service Info */}
            <div className="flex justify-between items-center">
              <span className="text-lg capitalize">
                {data?.ticket_details.service.name}
              </span>
              <span className="text-[#B98E47]">
                Counter Code: {data?.ticket_details.counter_code||data?.ticket_details.counter_id}
              </span>
            </div>

            {/* Details List */}
            <div className="space-y-4">
              <div className="flex items-center w-full">
                <div>
                  {" "}
                  <div className="w-10 h-10 rounded-full bg-[#FFFCF5] border border-[#B98E47] flex items-center justify-center mr-4">
                    <UserIcon className="h-5 w-5 text-[#B98E47]" />
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500 md:text-base">
                    <span className="font-normal">Title: </span>
                    <span className="break-all whitespace-pre-wrap">
                      {data?.ticket_details.title}
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex items-center w-full">
                <div>
                  {" "}
                  <div className="w-10 h-10 rounded-full bg-[#FFFCF5] border border-[#B98E47] flex items-center justify-center mr-4">
                    <UserIcon className="h-5 w-5 text-[#B98E47]" />
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500 md:text-base">
                    <span className="font-normal">Description: </span>
                    <span className="break-all whitespace-pre-wrap">
                      {data?.ticket_details.description ?? "N/A"}
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-[#FFFCF5] border border-[#B98E47] flex items-center justify-center mr-4">
                  <CalendarIcon className="h-5 w-5 text-[#B98E47]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    Appointment Date:{" "}
                    {formatDate(data?.ticket_details.date ?? "")}
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-[#FFFCF5] border border-[#B98E47] flex items-center justify-center mr-4">
                  <Watch className="h-5 w-5 text-[#B98E47]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    Appointment Time:{" "}
                    {formatTime(data?.ticket_details.time ?? "")}
                  </p>
                </div>
              </div>
              {/* <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-[#FFFCF5] border border-[#B98E47] flex items-center justify-center mr-4">
                  <Mail className="h-5 w-5 text-[#B98E47]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                   
                  </p>
                </div>
              </div> */}

              <div className="flex items-center w-full">
                <div>
                  {" "}
                  <div className="w-10 h-10 rounded-full bg-[#FFFCF5] border border-[#B98E47] flex items-center justify-center mr-4">
                    <Mail className="h-5 w-5 text-[#B98E47]" />
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500 md:text-base">
                    <span className="font-normal">Email: </span>
                    <span className="break-all whitespace-pre-wrap">
                      Email:{" "}
                      {String(data?.ticket_details.customer.id) === "0"
                        ? ""
                        : data?.ticket_details.customer.email}
                    </span>
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-[#FFFCF5] border border-[#B98E47] flex items-center justify-center mr-4">
                  <Phone className="h-5 w-5 text-[#B98E47]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    Phone Number :{" "}
                    {String(data?.ticket_details.customer.id) === "0"
                      ? ""
                      : formatPhoneNumber(
                          data?.ticket_details.customer.phone_number ?? ""
                        )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-[#FFFCF5] border border-[#B98E47] flex items-center justify-center mr-2">
                  <Users className="h-5 w-5 text-[#B98E47]" />
                </div>
                <div className="">
                  <p className="text-sm text-muted-foreground capitalize">
                    Department : {""}
                    {data?.ticket_details.service.department_name}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-[#FFFCF5] border border-[#B98E47] flex items-center justify-center mr-2">
                  <CalendarIcon className="h-5 w-5 text-[#B98E47]" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">
                    Ticket Date : {""}
                    {new Date(`${data?.ticket_details.date ?? ""}`)
                      .toLocaleDateString("en-PK", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
                .replace(/\//g, "-")
                      
                      }
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-[#FFFCF5] border border-[#B98E47] flex items-center justify-center mr-2">
                  <Clock className="h-5 w-5 text-[#B98E47]" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">
                    Last Update at : {""}
                    {new Date(`${data?.ticket_details.updated_at ?? ""}Z`)
                      .toLocaleDateString("en-PK", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })
                          .replace(/\//g, "-")}
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-[#FFFCF5] border border-[#B98E47] flex items-center justify-center mr-4">
                  <Clock className="h-5 w-5 text-[#B98E47]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    Waiting Time:{" "}
                    {data?.ticket_details.served_at
                      ? (() => {
                          const waitingMinutes = calculateWaitingTime(
                            data?.ticket_details.date,
                            data?.ticket_details.time,
                            data?.ticket_details.served_at
                          );
                          return waitingMinutes >= 60
                            ? `${Math.floor(waitingMinutes / 60)} hr${waitingMinutes % 60 !== 0 ? ` ${waitingMinutes % 60} min` : ""}`
                            : waitingMinutes > 0
                              ? `${waitingMinutes} min`
                              : "0 min";
                        })()
                      : ""}
                  </p>
                </div>
              </div>
            </div>

            {data?.ticket_details.ticket_feedback &&
              data.ticket_details.ticket_feedback.rating !== null &&
              data.ticket_details.ticket_feedback.rating >= 1 && (
                <div className="text-gray-400 text-sm">
                  <div className=" p-3 border rounded-lg bg-secondary shadow-sm text-black">
                    Feedback :{" "}
                    <p className="text-sm md:text-base break-words whitespace-pre-line text-black">
                      {data?.ticket_details.ticket_feedback?.feedback}
                    </p>
                    <div className="flex gap-2 items-center">
                      Rating:{" "}
                      <div className="flex items-center">
                        {renderStars(
                          data?.ticket_details.ticket_feedback.rating ?? 0
                        )}
                      </div>
                    </div>
                    {/* Wait Time */}
                    <div>
                      Wait Time:{" "}
                      {data?.ticket_details.ticket_feedback.wait_for_long_time
                        ? "Yes"
                        : "No"}
                    </div>
                  </div>
                </div>
              )}

            {data?.ticket_details.cancel_reason?.length && (
              <div className="text-gray-400 text-sm">
                <div className=" p-3 border rounded-lg bg-secondary shadow-sm text-black">
                  Cancel Reason :{" "}
                  <p className="text-sm md:text-base break-words whitespace-pre-line text-black">
                    {data?.ticket_details?.cancel_reason}
                  </p>
                </div>
              </div>
            )}
            {/* Linked Tickets Section */}
            {(data?.ticket_details.parent_ticket ||
              (data?.ticket_details.child_tickets &&
                data?.ticket_details.child_tickets.length > 0)) && (
              <div className="text-sm">
                <div className="p-3 border rounded-lg bg-secondary shadow-sm">
                  <h3 className="font-medium mb-2">Linked Tickets</h3>

                  {/* Show parent ticket if this is a child ticket */}
                  {data?.ticket_details.parent_ticket && (
                    <div className="flex items-center justify-between p-2 rounded-md cursor-pointer mb-2">
                      <div>
                        <span className="font-medium">Previous Ticket: </span>
                        <span>
                          {data.ticket_details.parent_ticket.ticket_id}
                        </span>
                        <div className="text-xs text-muted-foreground mt-1 break-words whitespace-pre-line">
                          {data.ticket_details.parent_ticket.title}
                        </div>
                      </div>
                      <div
                        className={`px-2 py-1 text-xs rounded-full text-white ${getStatusColor(data.ticket_details.parent_ticket.status)}`}
                      >
                        {data.ticket_details.parent_ticket.status.replace(
                          "_",
                          " "
                        )}
                      </div>
                    </div>
                  )}

                  {/* Show child tickets if this is a parent ticket */}
                  {data?.ticket_details.child_tickets &&
                    data.ticket_details.child_tickets.length > 0 && (
                      <div className="space-y-2">
                        <p className="font-medium mb-1">Next Linked Tickets:</p>
                        {data.ticket_details.child_tickets.map(
                          (childTicket) => (
                            <div
                              key={childTicket.id}
                              className="flex items-center justify-between p-2 rounded-md cursor-pointer"
                            >
                              <div>
                                <span>{childTicket.ticket_number}</span>
                                <div className="text-xs text-muted-foreground mt-1 break-words whitespace-pre-line">
                                  {childTicket.title}
                                </div>
                              </div>
                              <div
                                className={`px-2 py-1 text-xs rounded-full text-white ${getStatusColor(childTicket.status)}`}
                              >
                                {childTicket.status.replace("_", " ")}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    )}
                </div>
              </div>
            )}
            {/* Comments Section */}
            <div className="pt-2 space-y-4">
              {(data?.ticket_details.comments?.length ?? 0) > 0 ? (
                data?.ticket_details.comments?.map((comment: Comment) => (
                  <div
                    key={comment.id}
                    className=" p-3 border rounded-lg bg-secondary shadow-sm"
                  >
                    <p className="text-sm md:text-base break-words whitespace-pre-line">
                      {comment.comment}
                    </p>

                    <div className="w-full flex justify-end items-end flex-col">
                      <p className="text-xs md:text-sm font-semibold mt-1 capitalize">
                        Comment By: {comment.staff_username}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {(() => {
                          const date = new Date(comment.created_at + "Z");
                          const options: Intl.DateTimeFormatOptions = {
                            timeZone: "Asia/Karachi",
                            year: "numeric",
                            day: "2-digit",
                            month: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          };

                          const [datePart, timePart] = date
                            .toLocaleString("en-PK", options)
                            .split(", ");
                          const [month, day, year] = datePart.split("/");

                          return `${day}-${year}-${month}, ${timePart}`;
                        })()}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">
                  No comments available.
                </p>
              )}
            </div>

            {/* Updated Buttons Here */}
            {/* Action Buttons */}
          </CardContent>
        </Card>
      </main>
      {/* ShadCN Dialog for canceling ticket */}
      {iscancel && (
        <AlertDialog open={iscancel} onOpenChange={() => setIsCancel(false)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogDescription>
                <div className="flex flex-col items-start  justify-start gap-4">
                  <img src={warning} alt="warning" className="size-14" />
                  {/* <AlertDialogTitle className="text-lg font-medium">
                    Are you sure you want to cancel this Ticket? This action
                    cannot be undone.
                  </AlertDialogTitle> */}
                </div>
                <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
                  <div className="space-y-2">
                    <label
                      htmlFor="cancelReason"
                      className="block text-sm font-semibold text-start"
                    >
                      Reason for Cancellation
                    </label>
                    <Controller
                      control={control}
                      name="cancel_reason"
                      rules={{
                        required: "Cancel reason is required",
                        maxLength: {
                          value: 350,
                          message: "Maximum of 350 characters allowed",
                        },
                      }}
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          name="cancel_reason"
                          placeholder="Enter your cancellation reason"
                          className="w-full"
                        />
                      )}
                    />
                    {errors.cancel_reason && (
                      <p className="text-red-500 text-xs text-start">
                        {errors.cancel_reason.message?.toString()}
                      </p>
                    )}
                  </div>

                  <div className="flex justify-end items-center gap-3 mt-4">
                    <AlertDialogCancel
                      onClick={() => {
                        setIsCancel(false);
                        clearCustomTagsFromStorage();
                      }}
                      className="w-[100px] border border-primary font-semibold"
                    >
                      <i className="fa fa-xmark text-base px-1 "></i> No
                    </AlertDialogCancel>
                    <Button
                      type="submit"
                      className="w-[100px] bg-primary mt-2 !text-black font-semibold"
                    >
                      <i className="fa-regular fa-floppy-disk text-base px-1 "></i>{" "}
                      Yes
                    </Button>
                  </div>
                </form>
              </AlertDialogDescription>
            </AlertDialogHeader>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {isupdatingTicket && (
        <AlertDialog open={isupdatingTicket} onOpenChange={setIsUpdatingTicket}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogDescription>
                <div className="flex flex-row items-center justify-start gap-4">
                  <Ticket className="size-10" />
                  <AlertDialogTitle className="text-sm sm:text-base font-medium text-left">
                    Provide the Details to Update.
                  </AlertDialogTitle>
                </div>
                {/* Form */}
                <form
                  onSubmit={tickethandleSubmit(onHandleUpdateInfo)}
                  className="mt-4 space-y-4"
                >
                  <div className="space-y-2 w-full">
                    <Label htmlFor="title" className="block text-left">
                      Title
                    </Label>
                    <div className="relative">
                      <Controller
                        name="title"
                        rules={{
                          required: "Title is required",
                          maxLength: {
                            value: 50,
                            message: "Title cannot exceed 50 characters",
                          },
                        }}
                        control={ticketcontrol}
                        render={({ field }) => (
                          <>
                            {" "}
                            <Input
                              {...field}
                              value={searchValue || field.value}
                              onChange={(e) => {
                                field.onChange(e);
                                setSearchValue(e.target.value);
                              }}
                              disabled={
                                field.value === TicketStatus.AWAITING_INFO ||
                                field.value === TicketStatus.RESERVED
                              }
                              onClick={() => setIsCommandOpen(true)}
                              placeholder="Search or add a title"
                              className="w-full border rounded p-2"
                            />
                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex">
                              {searchValue && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setValue("title", "");
                                    setUpdateTicketValue("title", "");
                                    setSearchValue("");
                                  }}
                                  className="mr-1 p-1 h-auto"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="p-1 h-auto"
                                onClick={() => setIsCommandOpen(true)}
                              >
                                <ChevronDown className="h-4 w-4" />
                              </Button>
                            </div>
                          </>
                        )}
                      />
                      <Button
                        type="button"
                        size="sm"
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white"
                        onClick={() => setIsCommandOpen(true)}
                      >
                        <ChevronDown className="h-4 w-4 text-black" />
                      </Button>
                    </div>
                    {tickethandleErrors.title && (
                      <p className="text-red-500 text-xs text-left">
                        {tickethandleErrors.title.message}
                      </p>
                    )}
                  </div>

                  {/* Command Dialog for tag selection */}
                  <CommandDialog
                    open={isCommandOpen}
                    onOpenChange={setIsCommandOpen}
                  >
                    <CommandInput
                      value={manualTagValue}
                      onValueChange={setManualTagValue}
                      placeholder="Search Title or add new..."
                    />
                    <CommandList>
                      <CommandEmpty>
                        {manualTagValue ? (
                          <div
                            onClick={() => {
                              handleTagSelect(manualTagValue);
                              setManualTagValue("");
                              handleManualTagSubmit();
                              setSearchValue("");
                            }}
                            className="px-2 py-3 flex justify-between items-center"
                          >
                            <span className="flex flex-row break-all items-center ">
                              <Plus className="h-5 w-5 text-lg font-bold " />
                              Add "{manualTagValue}"
                            </span>
                          </div>
                        ) : (
                          <div className="px-2 py-3">No title found.</div>
                        )}
                      </CommandEmpty>
                      <CommandGroup heading="Available Titles">
                        {[
                          ...(service?.tags || []),
                          rescheduleWatcher.title,
                          ticketUpdateWacther.title,
                        ]
                          .filter(
                            (tag, index, self) =>
                              self.indexOf(tag) === index &&
                              tag
                                ?.toLowerCase()
                                .includes(manualTagValue.toLowerCase())
                          )
                          .map((tag) => (
                            <CommandItem
                              key={tag}
                              value={tag}
                              onSelect={() => handleTagSelect(tag)}
                              className="capitalize"
                            >
                              <div className="w-full whitespace-normal break-words py-1">
                                {tag}
                              </div>
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </CommandDialog>
                  {/* Description Field */}
                  <div className="space-y-2">
                    <Label htmlFor="description" className="block text-left">
                      Description
                    </Label>
                    <Controller
                      name="description"
                      control={ticketcontrol}
                      rules={{
                        maxLength: {
                          value: 350,
                          message: "description cannot exceed 350 characters",
                        },
                      }}
                      render={({ field }) => (
                        <textarea
                          {...field}
                          rows={6}
                          placeholder="Enter description (optional)"
                          className="w-full border p-2 rounded-lg min-h-[120px] text-left"
                        />
                      )}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end items-center gap-3 mt-4">
                    <AlertDialogCancel
                      onClick={() => {
                        setIsUpdatingTicket(false);
                        clearCustomTagsFromStorage();
                        reset({
                          title: "",
                          description: "",
                          status: TicketStatus.NEW,
                          ticketId: 0,
                        });
                      }}
                      className="w-[100px] border border-primary font-semibold"
                    >
                      Cancel
                    </AlertDialogCancel>

                    <Button
                      type="submit"
                      disabled={isTicketLoading}
                      className="w-[140px] bg-primary gap-2 text-black font-semibold"
                    >
                      {isTicketLoading && (
                        <i className="animate-spin border-4 border-gray-300 border-t-green-500 rounded-full w-6 h-6 inline-block pr-2 pl-2"></i>
                      )}
                      {isTicketLoading ? "Submitting" : "Submit"}
                    </Button>
                  </div>
                </form>
              </AlertDialogDescription>
            </AlertDialogHeader>
          </AlertDialogContent>
        </AlertDialog>
      )}

      <FormProvider {...createTicketForm}>
        <TicketForm
          departmentId={serviceId ? Number(serviceId) : undefined}
          open={isCreateTicketOpen}
          onClose={() => setIsCreateTicketOpen(false)}
          onTicketCreated={async (ticket) => {
            const parentTicketId = data?.ticket_details?.id;
            if (!parentTicketId) return;
            try {
              await addLinkedTicket({
                action: TicketAction.LINK,
                ticket_id: Number(ticket.id),
                staff_id: String(userInfo?.id ?? 0),
                trigger_lambda: false,
                parentTicketId: Number(parentTicketId),
              }).unwrap();
              refetch();
            } catch (err) {
              toast({
                variant: "destructive",
                title: "Failed to link ticket",
                description: "Ticket was created but linking failed.",
              });
            }
          }}
        />
      </FormProvider>

      <FormProvider {...rescheduleForm}>
        <TicketForm
          departmentId={serviceId ? Number(serviceId) : undefined}
          open={isRescheduleOpen}
          onClose={() => setIsRescheduleOpen(false)}
          onTicketCreated={async () => {
            const previousTicketId = data?.ticket_details?.id;
            if (!previousTicketId) return;
            try {
              await cancelTicket({
                ticket_ids: [Number(previousTicketId)],
                cancel_reason: "Ticket Scheduled to later date.",
              }).unwrap();
              toast({
                title: "Ticket Rescheduled",
                description: "Ticket Rescheduled Successfully",
                variant: "success",
              });
              refetch();
              navigate(`/user/ticket-listing`);
            } catch (err) {
              toast({
                variant: "destructive",
                title: "Reschedule failed",
                description: "Ticket was created but previous ticket cancellation failed.",
              });
            }
          }}
        />
      </FormProvider>
    </div>
  );
}
